import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Layout,
  ArrowRight,
  FileText,
  Cloud,
  MessageSquare,
  Database,
  BarChart2,
  Map as MapIcon,
  Plus
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { workspaceApi } from '../api/workspaceApi';
import { datasetApi } from '../api/datasetApi';
import { dashboardApi } from '../api/dashboardApi';
import { showErrorToast, showSuccessToast } from '../lib/toast';
import CreateWorkspaceWizard from '../components/workspace/CreateWorkspaceWizard';

const ICON_MAP = {
  MessageSquare,
  Database,
  BarChart2,
  MapIcon
};

const StatCard = ({ label, value, max, unit, icon: Icon, color }) => (
  <div className="bg-white dark:bg-gray-900 p-6 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300 group">
    <div className="flex items-center justify-between mb-4">
      <div className={cn("p-2 rounded-xl transition-transform group-hover:scale-110", color)}>
        <Icon size={20} />
      </div>
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{label}</p>
      <div className="flex items-baseline">
        <span className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">{value}</span>
        {unit && <span className="text-xs font-bold text-gray-400 ml-1">{unit}</span>}
        {max && (
          <span className="text-xs font-bold text-gray-400 font-medium ml-1">/ {max === Infinity ? '∞' : max}</span>
        )}
      </div>
    </div>
    {max && max !== Infinity && (
      <div className="mt-4 h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all duration-1000", color.replace('text-', 'bg-'))}
          style={{ width: `${(parseFloat(value) / parseFloat(max)) * 100}%` }}
        />
      </div>
    )}
  </div>
);

const RecentWorkspaceCard = ({ id, name, sources, lastAccessed, onClick, chatsCount }) => {
  return (
    <button
      onClick={() => onClick(id)}
      className="group flex flex-col p-5 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl text-left hover:border-emerald-500/50 transition-all duration-300"
    >
      <div className="p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-400 group-hover:text-emerald-600 transition-colors mb-4 w-fit">
        <Layout size={20} />
      </div>
      <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight mb-4 truncate w-full group-hover:text-emerald-600 transition-colors">
        {name}
      </h3>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-1">
          <Database size={12} className="text-gray-400" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">
            {sources?.length || 0}
          </span>
        </div>
        <div className="flex items-center gap-1 border-l border-gray-100 dark:border-gray-800 pl-3">
          <MessageSquare size={12} className="text-gray-400" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">
            {chatsCount || 0}
          </span>
        </div>
      </div>

      <div className="mt-auto pt-3 border-t border-gray-50 dark:border-gray-800 flex items-center justify-between w-full">
        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tight">{lastAccessed}</span>
        <ArrowRight size={14} className="text-gray-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
      </div>
    </button>
  );
};

const RecentDatasetRow = ({ name, type }) => (
  <div className="flex items-center gap-4 py-3 border-b border-gray-50 dark:border-gray-800 last:border-0">
    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-400">
      {type === 'csv' || type === 'parquet' ? <FileText size={16} /> : <Cloud size={16} />}
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate uppercase tracking-tight">{name}</h4>
      <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{type}</p>
    </div>
  </div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  useEffect(() => {
    document.title = 'manteo.ai | Home';
  }, []);

  const navigate = useNavigate();
  const [recentWorkspaces, setRecentWorkspaces] = useState([]);
  const [recentDatasets, setRecentDatasets] = useState([]);
  const [stats, setStats] = useState(null);
  const [systemStatus, setSystemStatus] = useState('checking');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const loadHomeData = async () => {
    setIsLoading(true);
    try {
      const [workspaces, dashboardStats, allDatasets, healthData] = await Promise.all([
        workspaceApi.getWorkspaces(),
        dashboardApi.getStats(),
        datasetApi.getDatasets(),
        dashboardApi.getHealthDetailed()
      ]);

      setRecentWorkspaces(workspaces.slice(0, 3));
      setStats(dashboardStats);
      setRecentDatasets(allDatasets.slice(0, 4));
      setSystemStatus(healthData.status === 'ok' ? 'live' : 'down');
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setSystemStatus('down');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHomeData();
  }, []);

  const dashboardStats = [
    {
      label: "Monthly Messages",
      value: stats?.monthly_messages?.value ?? '0',
      max: Infinity,
      icon: 'MessageSquare',
      color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20"
    },
    {
      label: "Cloud Storage",
      value: stats?.cloud_storage?.value ?? '0',
      max: Infinity,
      unit: "GB",
      icon: 'Database',
      color: "text-blue-600 bg-blue-50 dark:bg-blue-900/20"
    },
    {
      label: "Plots Generated",
      value: stats?.plots_generated?.value ?? '0',
      unit: "Total",
      icon: 'BarChart2',
      color: "text-amber-600 bg-amber-50 dark:bg-amber-900/20"
    },
    {
      label: "Map Layers",
      value: stats?.map_layers?.value ?? '0',
      unit: stats?.map_layers?.unit ?? 'Total',
      icon: 'MapIcon',
      color: "text-purple-600 bg-purple-50 dark:bg-purple-900/20"
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 dark:bg-gray-950 px-8 py-12 transition-colors duration-300">
      <CreateWorkspaceWizard
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={async (workspaceData) => {
          try {
            const newWs = await workspaceApi.createWorkspace(workspaceData);
            showSuccessToast('Workspace created.');
            navigate(`/workspace/${newWs.id}`);
          } catch (err) {
            console.error('Failed to create workspace:', err);
            showErrorToast('Could not create workspace. Please try again.');
            throw err;
          }
        }}
      />
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-4xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-2">
              Welcome Back, {user?.email?.split('@')[0] || 'User'}
            </h1>
            <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-[0.2em] flex items-center gap-2">
              {systemStatus === 'live' ? (
                <>
                  <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                  System Connected
                </>
              ) : systemStatus === 'checking' ? (
                <>
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                  Checking System...
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                  System Down
                </>
              )}
            </p>
          </div>
        </header>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {dashboardStats.map((stat, i) => (
            <StatCard
              key={i}
              {...stat}
              icon={ICON_MAP[stat.icon] || MessageSquare}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Recent Workspaces</h2>
              <Link to="/workspaces" className="text-[10px] font-black text-emerald-600 uppercase tracking-widest hover:underline">View All</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isLoading ? (
                [1, 2, 3, 4].map(i => <div key={i} className="h-40 rounded-2xl bg-gray-100 dark:bg-gray-900 animate-pulse" />)
              ) : recentWorkspaces.length > 0 ? (
                <>
                  {recentWorkspaces.map(w => (
                    <RecentWorkspaceCard key={w.id} {...w} onClick={(id) => navigate(`/workspace/${id}`)} />
                  ))}
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex flex-col p-5 bg-white dark:bg-gray-900 border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-2xl text-left hover:border-emerald-500/50 hover:bg-emerald-50/10 transition-all duration-300 group items-center justify-center min-h-[160px]"
                  >
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl text-emerald-600 mb-3 group-hover:scale-110 transition-transform">
                      <Plus size={24} strokeWidth={3} />
                    </div>
                    <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">Add New Workspace</span>
                  </button>
                </>
              ) : (
                <div className="col-span-full py-12 flex flex-col items-center justify-center border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-3xl gap-4">
                  <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">No workspaces found</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Recent Data</h2>
              <Link to="/sources" className="text-[10px] font-black text-emerald-600 uppercase tracking-widest hover:underline">Manage All</Link>
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
              {isLoading ? (
                [1, 2, 3, 4].map(i => <div key={i} className="h-12 w-full bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse mb-3" />)
              ) : (
                recentDatasets.map((d, i) => (
                  <RecentDatasetRow key={i} {...d} />
                ))
              )}
              {!isLoading && recentDatasets.length === 0 && (
                <p className="text-center text-xs font-bold text-gray-400 uppercase py-8">No data connected</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
